import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { sectionsApi, CreateSectionRequest, UpdateSectionRequest, CreateSubsectionRequest, UpdateSubsectionRequest } from '../../services/sectionsApi';
import {
  setLoading,
  setBulkLoading,
  setError,
  setSections,
  setSelectedSection,
  setRoles,
  setSubsections,
  addSection,
  updateSection,
  removeSection,
  addSubsection,
  updateSubsection,
  removeSubsection,
  setDialogOpen,
  setSubsectionDialogOpen,
  setValidationErrors,
  setSubsectionValidationErrors,
  setStatistics,
  deselectAllSections,
  FETCH_SECTIONS,
  FETCH_SECTION_BY_ID,
  CREATE_SECTION,
  UPDATE_SECTION,
  DELETE_SECTION,
  FETCH_ROLES,
  CREATE_SUBSECTION,
  UPDATE_SUBSECTION,
  DELETE_SUBSECTION,
  FETCH_SUBSECTIONS,
  BULK_ACTIVATE_SECTIONS,
  BULK_DEACTIVATE_SECTIONS,
  FETCH_SECTIONS_STATISTICS,
} from '../reducers/sectionsSlice';
import { RootState } from '../index';

// Worker Sagas
function* fetchSectionsSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const sections = yield call(sectionsApi.getAll);
    yield put(setSections(sections));
  } catch (error: any) {
    console.error('Error fetching sections:', error);
    yield put(setError(error.message || 'Failed to fetch sections'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchSectionByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const section = yield call(sectionsApi.getById, action.payload);
    
    // Parse roles if they're in string format
    if (section.Roles && typeof section.Roles === 'string') {
      try {
        section.Roles = JSON.parse('[' + section.Roles + ']');
      } catch (e) {
        section.Roles = [];
      }
    }
    
    yield put(setSelectedSection(section));
    
    // Fetch subsections for this section
    if (section.Id) {
      yield call(fetchSubsectionsSaga, { payload: section.Id } as PayloadAction<number>);
    }
  } catch (error: any) {
    console.error('Error fetching section:', error);
    yield put(setError(error.message || 'Failed to fetch section'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createSectionSaga(action: PayloadAction<CreateSectionRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the section
    const validationErrors = yield call(sectionsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newSection = yield call(sectionsApi.create, action.payload);
    yield put(addSection(newSection));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchSectionsStatisticsSaga);
  } catch (error: any) {
    console.error('Error creating section:', error);
    yield put(setError(error.message || 'Failed to create section'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateSectionSaga(action: PayloadAction<UpdateSectionRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the section
    const validationErrors = yield call(sectionsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedSection = yield call(sectionsApi.update, action.payload);
    yield put(updateSection(updatedSection));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchSectionsStatisticsSaga);
  } catch (error: any) {
    console.error('Error updating section:', error);
    yield put(setError(error.message || 'Failed to update section'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteSectionSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(sectionsApi.delete, action.payload);
    yield put(removeSection(action.payload));
    
    // Refresh statistics
    yield call(fetchSectionsStatisticsSaga);
  } catch (error: any) {
    console.error('Error deleting section:', error);
    yield put(setError(error.message || 'Failed to delete section'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchRolesSaga(): Generator<any, void, any> {
  try {
    const roles = yield call(sectionsApi.getRoles);
    yield put(setRoles(roles));
  } catch (error: any) {
    console.error('Error fetching roles:', error);
    yield put(setError(error.message || 'Failed to fetch roles'));
  }
}

function* createSubsectionSaga(action: PayloadAction<CreateSubsectionRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the subsection
    const validationErrors = yield call(sectionsApi.validation.validateSubsection, action.payload);
    if (validationErrors.length > 0) {
      yield put(setSubsectionValidationErrors(validationErrors));
      return;
    }
    
    const newSubsection = yield call(sectionsApi.createSubsection, action.payload);
    yield put(addSubsection(newSubsection));
    yield put(setSubsectionDialogOpen(false));
    yield put(setSubsectionValidationErrors([]));
  } catch (error: any) {
    console.error('Error creating subsection:', error);
    yield put(setError(error.message || 'Failed to create subsection'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateSubsectionSaga(action: PayloadAction<UpdateSubsectionRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the subsection
    const validationErrors = yield call(sectionsApi.validation.validateSubsection, action.payload);
    if (validationErrors.length > 0) {
      yield put(setSubsectionValidationErrors(validationErrors));
      return;
    }
    
    const updatedSubsection = yield call(sectionsApi.updateSubsection, action.payload);
    yield put(updateSubsection(updatedSubsection));
    yield put(setSubsectionDialogOpen(false));
    yield put(setSubsectionValidationErrors([]));
  } catch (error: any) {
    console.error('Error updating subsection:', error);
    yield put(setError(error.message || 'Failed to update subsection'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteSubsectionSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(sectionsApi.deleteSubsection, action.payload);
    yield put(removeSubsection(action.payload));
  } catch (error: any) {
    console.error('Error deleting subsection:', error);
    yield put(setError(error.message || 'Failed to delete subsection'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchSubsectionsSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    const subsections = yield call(sectionsApi.getSubsectionsBySectionId, action.payload);
    yield put(setSubsections(subsections));
  } catch (error: any) {
    console.error('Error fetching subsections:', error);
    // Don't show error for subsections as it's not critical
  }
}

function* bulkActivateSectionsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(sectionsApi.bulkActivate, action.payload);
    yield put(deselectAllSections());
    
    // Refresh the sections list
    yield call(fetchSectionsSaga);
    yield call(fetchSectionsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk activating sections:', error);
    yield put(setError(error.message || 'Failed to activate sections'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* bulkDeactivateSectionsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(sectionsApi.bulkDeactivate, action.payload);
    yield put(deselectAllSections());
    
    // Refresh the sections list
    yield call(fetchSectionsSaga);
    yield call(fetchSectionsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk deactivating sections:', error);
    yield put(setError(error.message || 'Failed to deactivate sections'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* fetchSectionsStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(sectionsApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching sections statistics:', error);
    // Don't show error for statistics as it's not critical
  }
}

// Helper saga to auto-fetch roles when needed
function* autoFetchRoles(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    if (state.sections.roles.length === 0) {
      yield call(fetchRolesSaga);
    }
  } catch (error) {
    // Ignore errors in auto-fetch
  }
}

// Watcher Sagas
function* watchFetchSections(): Generator<any, void, any> {
  yield takeLatest(FETCH_SECTIONS, fetchSectionsSaga);
}

function* watchFetchSectionById(): Generator<any, void, any> {
  yield takeEvery(FETCH_SECTION_BY_ID, fetchSectionByIdSaga);
}

function* watchCreateSection(): Generator<any, void, any> {
  yield takeEvery(CREATE_SECTION, function*(action: PayloadAction<CreateSectionRequest>): Generator<any, void, any> {
    yield call(autoFetchRoles);
    yield call(createSectionSaga, action);
  });
}

function* watchUpdateSection(): Generator<any, void, any> {
  yield takeEvery(UPDATE_SECTION, function*(action: PayloadAction<UpdateSectionRequest>): Generator<any, void, any> {
    yield call(autoFetchRoles);
    yield call(updateSectionSaga, action);
  });
}

function* watchDeleteSection(): Generator<any, void, any> {
  yield takeEvery(DELETE_SECTION, deleteSectionSaga);
}

function* watchFetchRoles(): Generator<any, void, any> {
  yield takeLatest(FETCH_ROLES, fetchRolesSaga);
}

function* watchCreateSubsection(): Generator<any, void, any> {
  yield takeEvery(CREATE_SUBSECTION, createSubsectionSaga);
}

function* watchUpdateSubsection(): Generator<any, void, any> {
  yield takeEvery(UPDATE_SUBSECTION, updateSubsectionSaga);
}

function* watchDeleteSubsection(): Generator<any, void, any> {
  yield takeEvery(DELETE_SUBSECTION, deleteSubsectionSaga);
}

function* watchFetchSubsections(): Generator<any, void, any> {
  yield takeLatest(FETCH_SUBSECTIONS, fetchSubsectionsSaga);
}

function* watchBulkActivateSections(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE_SECTIONS, bulkActivateSectionsSaga);
}

function* watchBulkDeactivateSections(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE_SECTIONS, bulkDeactivateSectionsSaga);
}

function* watchFetchSectionsStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_SECTIONS_STATISTICS, fetchSectionsStatisticsSaga);
}

// Root saga
export function* sectionsSaga(): Generator<any, void, any> {
  yield all([
    watchFetchSections(),
    watchFetchSectionById(),
    watchCreateSection(),
    watchUpdateSection(),
    watchDeleteSection(),
    watchFetchRoles(),
    watchCreateSubsection(),
    watchUpdateSubsection(),
    watchDeleteSubsection(),
    watchFetchSubsections(),
    watchBulkActivateSections(),
    watchBulkDeactivateSections(),
    watchFetchSectionsStatistics(),
  ]);
} 